import type {
    HistorySnapshot,
    PendingChangeItem,
} from "@/types/visual-editor-history.types";

/**
 * Get all pending changes grouped by element
 * Returns Map with elementId as key and latest state of each element
 */
function getAllPendingChanges(
    history: HistorySnapshot[],
    currentIndex: number
): Map<string, PendingChangeItem> {
    const changesMap = new Map<string, PendingChangeItem>();

    // If currentIndex is -1, we're before any changes (original state)
    if (currentIndex < 0) {
        return changesMap; // Return empty map - no pending changes
    }

    const relevantHistory = history.slice(0, currentIndex + 1);

    relevantHistory.forEach((snapshot) => {
        const existing = changesMap.get(snapshot.elementId);

        // Merge changes instead of overwriting
        // This ensures that style changes are preserved when followed by position changes
        const mergedChanges = {
            text:
                snapshot.pendingChanges.text !== undefined
                    ? snapshot.pendingChanges.text
                    : existing?.changes.text,
            className:
                snapshot.pendingChanges.className !== undefined
                    ? snapshot.pendingChanges.className
                    : existing?.changes.className,
            styles: {
                ...(existing?.changes.styles || {}),
                ...(snapshot.pendingChanges.styles || {}),
            },
            attributes: {
                ...(existing?.changes.attributes || {}),
                ...(snapshot.pendingChanges.attributes || {}),
            },
        };

        // Preserve children and iconName from earlier snapshots (e.g., layout add has children,
        // but a subsequent move snapshot rebuilds from DOM without children/iconName)
        const mergedElement = {
            ...snapshot.fullElementData,
            children: snapshot.fullElementData.children || existing?.element.children,
            iconName: snapshot.fullElementData.iconName || existing?.element.iconName,
        };

        changesMap.set(snapshot.elementId, {
            element: mergedElement,
            changes: mergedChanges,
            // Preserve the original actionType — if element was "add"-ed first,
            // subsequent "update" snapshots must NOT overwrite it to "update",
            // otherwise the save logic skips the add-element branch.
            actionType: existing?.actionType || snapshot.actionType,
            // IMPORTANT: Preserve structureChange from ANY snapshot that has it
            // This ensures move operations are tracked even if followed by style/text updates
            structureChange:
                snapshot.structureChange || existing?.structureChange,
            // Keep the first original element state (when user first selected this element)
            originalElement:
                existing?.originalElement ||
                snapshot.originalElementData ||
                snapshot.fullElementData,
        });
    });

    return changesMap;
}

export { getAllPendingChanges };
